import { useState, useRef, useEffect } from "react"
import { Input as NextUIInput } from "@nextui-org/input"
import cx from "classnames"
import { axios } from "@/contexts/auth"
import { toast } from "react-toastify"

type HeadphoneBrand = {
  id: number
  name: string
}

type Headphone = {
  id: number
  brand: HeadphoneBrand
  model: string
}

type Props = {
  selectedBrandId: number | null
  value: string
  onChange: (value: string) => void
  onSelect: (headphone: Headphone | null) => void
  onCreateNew: (model: string) => Promise<Headphone>
  isInvalid?: boolean
  disabled?: boolean
}

const HeadphoneAutocomplete = ({
  selectedBrandId,
  value,
  onChange,
  onSelect,
  onCreateNew,
  isInvalid,
  disabled,
}: Props) => {
  const [suggestions, setSuggestions] = useState<Headphone[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Fetch suggestions when input changes or refresh is triggered
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!selectedBrandId || !value.trim()) {
        setSuggestions([])
        return
      }

      setIsLoading(true)
      try {
        const response = await axios.get("/api/equalizer/headphones/", {
          params: { brand: selectedBrandId },
        })
        // Filter headphones by brand and model name
        const filteredHeadphones = response.data
          .filter((headphone: Headphone) => headphone.brand.id === selectedBrandId)
          .filter((headphone: Headphone) =>
            headphone.model.toLowerCase().includes(value.toLowerCase())
          )
        setSuggestions(filteredHeadphones)
      } catch (error) {
        console.error("Error fetching headphone suggestions:", error)
        setSuggestions([])
      } finally {
        setIsLoading(false)
      }
    }

    const timeoutId = setTimeout(fetchSuggestions, 300) // Debounce API calls
    return () => clearTimeout(timeoutId)
  }, [value, selectedBrandId, refreshTrigger])

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    setShowSuggestions(true)
  }

  const handleSuggestionClick = (headphone: Headphone) => {
    onChange(headphone.model)
    onSelect(headphone)
    setShowSuggestions(false)
  }

  const handleCreateNew = async () => {
    if (!selectedBrandId || !value.trim()) return

    setIsCreating(true)
    try {
      const newHeadphone = await onCreateNew(value.trim())
      // Show success toast
      toast.success("Created Headphone")
      // Clear the input and suggestions
      onChange("")
      onSelect(null) // Clear the selected headphone to trigger validation
      setShowSuggestions(false)
      // Refresh the suggestions list
      setRefreshTrigger((prev) => prev + 1)
    } catch (error) {
      console.error("Error creating new headphone:", error)
      toast.error("Failed to create headphone")
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <NextUIInput
        variant="bordered"
        value={value}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        isDisabled={disabled}
        classNames={{
          inputWrapper: [
            "px-4 h-10",
            "border rounded-plus !shadow-inside-strong",
            isInvalid
              ? "bg-brown border-red group-data-[focus=true]:border-red data-[hover=true]:border-red animate-pulse"
              : "bg-cod-gray border-mine-shaft group-data-[focus=true]:border-silver data-[hover=true]:border-earth",
          ],
          input: [
            cx(
              "text-xs font-bulo font-bold text-ghost placeholder:italic",
              isInvalid
                ? "placeholder:text-dark-red placeholder:text-opacity-50"
                : "placeholder:text-earth"
            ),
          ],
        }}
        placeholder="Type to search or create new model"
      />
      {showSuggestions && (value.trim() || suggestions.length > 0) && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-plus border border-[#393939] bg-dark p-1.25 shadow-outside">
          {isLoading ? (
            <div className="px-2.5 py-1.5 text-sm text-silver">Loading...</div>
          ) : (
            <>
              {suggestions.map((headphone) => (
                <button
                  key={headphone.id}
                  className="w-full px-3 py-1.5 text-left font-bulo text-xs font-bold text-ghost hover:bg-mine-shaft hover:text-ghost"
                  onClick={() => handleSuggestionClick(headphone)}
                >
                  {headphone.model}
                </button>
              ))}
              {value.trim() &&
                !suggestions.some((h) => h.model.toLowerCase() === value.toLowerCase()) && (
                  <button
                    className="w-full px-3 py-1.5 text-left font-bulo text-xs font-bold text-ghost hover:bg-mine-shaft hover:text-ghost"
                    onClick={handleCreateNew}
                    disabled={isCreating}
                  >
                    {isCreating ? "Creating..." : `Create new model: "${value.trim()}"`}
                  </button>
                )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default HeadphoneAutocomplete
